/**
 * Future Expenses Page
 * 
 * Management of future expense goals with investment calculations.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, isBefore } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Plus,
  Search,
  Target,
  ArrowUpDown,
  Loader2,
  TrendingUp,
  Calendar,
  Wallet,
  RefreshCw,
  Play,
  AlertTriangle,
  Edit,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { CurrencyInput } from '@/components/ui/currency-input';
import { PercentageInput } from '@/components/ui/percentage-input';
import { Combobox } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DateInput } from '@/components/ui/date-input';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  FutureExpenseRow,
  CurrencyDisplay,
  FinancialSummaryCards,
  PeriodNavigator,
  BulkSelectionSummary,
} from '@/components/financial';
import { getBudgetPeriodPermissions, isDateWithinPeriod, formatDatePtBR } from '@/lib/date';
import { normalizeSearchText } from '@/lib/text';
import {
  useCurrentBudgetPeriod,
  useBudgetPeriodNavigation,
  useNavigatePeriod,
  useFutureExpenses,
  useCreateFutureExpense,
  useUpdateFutureExpense,
  useDeleteFutureExpense,
  useRealizeFutureExpense,
  useReplanFutureExpense,
  useExecuteFutureExpense,
  useFutureExpenseRealizationSheet,
  useFutureExpenseRealizations,
  useUpdateFutureExpenseRealization,
  useDeleteFutureExpenseRealization,
  useFutureExpenseDraftCalculation,
  useFinancialList,
  useInvestmentInstitutions,
  useCreateInvestmentInstitution,
  useAgents,
  useFinancialSettings,
} from '@/hooks/use-financial';
import { usePlanFeatures } from '@/hooks/use-plan-features';
import { FEATURES_FUTURE_EXPENSES } from '@/config/features';
import type {
  FutureExpense,
  FutureExpenseRealizationCreate,
  FutureExpenseCalculationRequest,
  FutureExpenseRealization,
} from '@/types/financial';
import { useDebounce } from '@/utils/performance';
import { toast } from '@/hooks/use-toast';
import {
  FIRST_ACCESS_TUTORIAL_DEMO,
  FIRST_ACCESS_TUTORIAL_STEPS,
  getTutorialDateInBudgetPeriod,
  getTutorialFutureDate,
  markTutorialKnownItemIds,
  findTutorialCreatedItem,
  requestTutorialAdvance,
  useActiveOnboardingStep,
} from '@/components/onboarding/tutorial-runtime';
import { formatBrasilia } from '@/lib/brasiliaDate';

const TUTORIAL_SCOPE = 'future-expenses';

// Realce do botão de criação quando o formulário está completo.
const CREATE_READY_BUTTON_CLASS =
  'bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-600';

// Poupança (aproximação): 0,50% a.m. (taxa efetiva). Pode ser ajustado futuramente via configuração.
const SAVINGS_MONTHLY_RATE_PERCENT = 0.5;

export function FutureExpensesPage() {
  const appliedTutorialStepRef = useRef<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<string[]>([]);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState<FutureExpense | null>(null);
  // Replanejamento: id da despesa de origem a excluir SOMENTE após a nova meta ser
  // criada. Enquanto a modal de criação estiver aberta (ou for cancelada), a despesa
  // original é preservada — cancelar não pode excluir o lançamento.
  const [replanSourceExpenseId, setReplanSourceExpenseId] = useState<string | null>(null);

  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isRealizeDialogOpen, setIsRealizeDialogOpen] = useState(false);
  const [isEditRealizedDialogOpen, setIsEditRealizedDialogOpen] = useState(false);
  const [isReplanDialogOpen, setIsReplanDialogOpen] = useState(false);
  const [isExecuteDialogOpen, setIsExecuteDialogOpen] = useState(false);
  const [isCalcDialogOpen, setIsCalcDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<FutureExpense | null>(null);
  const [selectedRealization, setSelectedRealization] = useState<FutureExpenseRealization | null>(null);
  const [calcExpenseId, setCalcExpenseId] = useState<string>('');
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    target_amount: '',
    target_date: '',
    present_value: '',
    monthly_payment: '',
    monthly_rate: '0.5',
    category: '',
    beneficiary_agent_id: '',
    notes: '',
  });

  const [realizeFormData, setRealizeFormData] = useState<FutureExpenseRealizationCreate>({
    amount: 0,
    realization_date: format(new Date(), 'yyyy-MM-dd'),
    investment_location: '',
    payment_method: '',
    agent_id: '',
    notes: '',
  });

  const [replanFormData, setReplanFormData] = useState({
    new_target_amount: '',
    new_target_date: '',
    reason: '',
  });

  const [executeFormData, setExecuteFormData] = useState({
    execution_date: format(new Date(), 'yyyy-MM-dd'),
    actual_amount_spent: 0,
    payment_method: '',
    notes: '',
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    target_amount: '',
    target_date: '',
    present_value: '',
    monthly_payment: '',
    monthly_rate: '0.5',
    category: '',
    beneficiary_agent_id: '',
    notes: '',
  });

  type ReverseCalcField = 'monthly_payment' | 'target_amount' | 'target_date' | 'present_value';
  const createCalculatedField: ReverseCalcField = 'monthly_payment';
  const [editCalculatedField, setEditCalculatedField] = useState<ReverseCalcField>('monthly_payment');

  // Período Orçamentário (contexto global)
  const { data: currentPeriod, isLoading: isPeriodLoading } = useCurrentBudgetPeriod();
  const { data: navigation } = useBudgetPeriodNavigation();
  const { mutate: navigatePeriod, isPending: isNavigating } = useNavigatePeriod();

  // Data hooks - usamos useFutureExpenses() sem status para buscar TODAS as metas
  // (ativas, alcançadas, concluídas, etc.) para que sejam exibidas nas abas correspondentes
  const { data: allExpenses, isLoading } = useFutureExpenses(undefined, currentPeriod?.id);
  const activeExpenses = (allExpenses || []).filter(e => e.status === 'active');
  const { data: categoriesList } = useFinancialList('expense_categories');
  const { data: paymentMethodsList } = useFinancialList('payment_methods');
  const { data: expenseNamesList } = useFinancialList('future_expense_names');
  const { data: investmentInstitutions } = useInvestmentInstitutions();
  const { mutate: createInvestmentInstitution } = useCreateInvestmentInstitution();
  const { data: agents } = useAgents();
  const expenseAgents = useMemo(() => agents || [], [agents]);
  const payerAgents = useMemo(() => {
    const allAgents = agents || [];
    const preferredAgents = allAgents.filter(
      (agent) => agent.is_default_payer || agent.is_default_agent
    );
    return preferredAgents.length > 0 ? preferredAgents : allAgents;
  }, [agents]);
  const defaultExpenseAgentId =
    expenseAgents.find((agent) => agent.is_default_agent)?.id ||
    expenseAgents[0]?.id ||
    '';
  const defaultPayerAgentId =
    payerAgents.find((agent) => agent.is_default_agent)?.id ||
    payerAgents.find((agent) => agent.is_default_payer)?.id ||
    payerAgents[0]?.id ||
    defaultExpenseAgentId;
  const { data: financialSettings } = useFinancialSettings();
  const navigate = useNavigate();
  const activeTutorialStep = useActiveOnboardingStep();

  const systemMonthlyRatePercent = useMemo(() => {
    const annualPercent = Number(financialSettings?.default_annual_rate ?? 0);
    if (!Number.isFinite(annualPercent) || annualPercent <= 0) return 0.5;
    const annualDecimal = annualPercent / 100;
    const monthlyDecimal = Math.pow(1 + annualDecimal, 1 / 12) - 1;
    const monthlyPercent = monthlyDecimal * 100;
    return Number.isFinite(monthlyPercent) ? monthlyPercent : 0.5;
  }, [financialSettings?.default_annual_rate]);

  const systemMonthlyRateLabel = `${systemMonthlyRatePercent.toFixed(2)}% a.m.`;

  // Gating por funcionalidade nos campos de Despesa Futura:
  // - "Investimento Inicial" (present_value) exige a feature `valor-presente`.
  //   Sem ela, o campo fica bloqueado e computa apenas 0,00.
  // - "Taxa de Juros (a.m.)" (monthly_rate) exige a feature `taxa-de-juros-mensal`.
  //   Sem ela, o campo fica bloqueado e usa apenas a taxa padrão do sistema.
  const { hasFeature } = usePlanFeatures();
  const canSetInitialInvestment = hasFeature(FEATURES_FUTURE_EXPENSES.VALOR_PRESENTE);
  const canSetInterestRate = hasFeature(FEATURES_FUTURE_EXPENSES.TAXA_DE_JUROS_MENSAL);

  const periodPermissions = getBudgetPeriodPermissions({
    startDate: currentPeriod?.start_date,
    endDate: currentPeriod?.end_date,
    paymentDay: financialSettings?.default_payment_day ?? currentPeriod?.payment_day,
  });
  const canPlanInPeriod = periodPermissions.canPlan;
  const canRealizeInPeriod = periodPermissions.canRealize;
  const tutorialContributionDate = getTutorialDateInBudgetPeriod(currentPeriod);
  const tutorialTargetDate = getTutorialFutureDate(currentPeriod);
  const tutorialCategory = categoriesList?.items?.[0]?.name || '';
  const tutorialFutureExpense = useMemo(
    () => findTutorialCreatedItem(TUTORIAL_SCOPE, allExpenses),
    [allExpenses]
  );
  
  // Mutation hooks
  const { mutate: createExpense, isPending: isCreating } = useCreateFutureExpense();
  const { mutate: updateExpense, isPending: isUpdating } = useUpdateFutureExpense();
  const { mutate: deleteExpense, mutateAsync: deleteExpenseAsync, isPending: isDeleting } =
    useDeleteFutureExpense();
  const { mutate: realizeExpense, isPending: isRealizing } = useRealizeFutureExpense();
  const { mutate: replanExpense, isPending: isReplanning } = useReplanFutureExpense();
  const { mutate: executeExpense, isPending: isExecuting } = useExecuteFutureExpense();
  const { data: realizationSheet, isLoading: isRealizationSheetLoading } = useFutureExpenseRealizationSheet(calcExpenseId);
  const { data: expenseRealizations, isLoading: isRealizationsLoading } = useFutureExpenseRealizations(selectedExpense?.id || '');
  const { mutate: updateRealization, isPending: isUpdatingRealization } = useUpdateFutureExpenseRealization();
  const { mutate: deleteRealization, isPending: isDeletingRealization } = useDeleteFutureExpenseRealization();
  // Reset form when dialog closes

  useEffect(() => {
    if (!isCreateDialogOpen) {
      setFormData({
        name: '',
        target_amount: '',
        target_date: '',
        present_value: '',
        monthly_payment: '',
        monthly_rate: systemMonthlyRatePercent.toFixed(2),
        category: '',
        beneficiary_agent_id: '',
        notes: '',
      });
      // Ao fechar a modal (inclusive por cancelamento), descarta o vínculo de
      // replanejamento para não excluir a despesa de origem indevidamente.
      setReplanSourceExpenseId(null);
    }
  }, [isCreateDialogOpen, systemMonthlyRatePercent]);

  // Defaults when opening the Create dialog (agente padrão + taxa do sistema)
  useEffect(() => {
    if (!isCreateDialogOpen) return;

    const defaultAgent = expenseAgents.find((agent) => agent.id === defaultExpenseAgentId);

    setFormData((prev) => {
      const nextBeneficiaryId = prev.beneficiary_agent_id || defaultAgent?.id || '';
      const nextMonthlyRate = systemMonthlyRatePercent.toFixed(2);

      if (prev.beneficiary_agent_id === nextBeneficiaryId && prev.monthly_rate === nextMonthlyRate) return prev;

      return {
        ...prev,
        beneficiary_agent_id: nextBeneficiaryId,
        monthly_rate: nextMonthlyRate,
      };
    });
  }, [defaultExpenseAgentId, expenseAgents, isCreateDialogOpen, systemMonthlyRatePercent]);

  // Enforcement do gating nos campos bloqueados (criação): garante que o valor
  // efetivo de present_value/monthly_rate seja o forçado, independentemente do
  // que estiver no input (defesa contra estado obsoleto ao alternar de plano).
  useEffect(() => {
    if (!isCreateDialogOpen) return;
    const sysRate = systemMonthlyRatePercent.toFixed(2);
    setFormData((prev) => {
      let next = prev;
      if (!canSetInitialInvestment && prev.present_value !== '0') {
        next = { ...next, present_value: '0' };
      }
      if (!canSetInterestRate && prev.monthly_rate !== sysRate) {
        next = { ...next, monthly_rate: sysRate };
      }
      return next;
    });
  }, [isCreateDialogOpen, canSetInitialInvestment, canSetInterestRate, systemMonthlyRatePercent]);

  // Enforcement do gating nos campos bloqueados (edição).
  useEffect(() => {
    if (!isEditDialogOpen) return;
    const sysRate = systemMonthlyRatePercent.toFixed(2);
    setEditFormData((prev) => {
      let next = prev;
      if (!canSetInitialInvestment && prev.present_value !== '0') {
        next = { ...next, present_value: '0' };
      }
      if (!canSetInterestRate && prev.monthly_rate !== sysRate) {
        next = { ...next, monthly_rate: sysRate };
      }
      return next;
    });
  }, [isEditDialogOpen, canSetInitialInvestment, canSetInterestRate, systemMonthlyRatePercent]);

  useEffect(() => {
    const stepId = FIRST_ACCESS_TUTORIAL_STEPS.futureSave;

    if (activeTutorialStep !== stepId) {
      if (appliedTutorialStepRef.current === stepId) {
        appliedTutorialStepRef.current = null;
      }
      return;
    }

    if (!canPlanInPeriod || appliedTutorialStepRef.current === stepId) return;

    appliedTutorialStepRef.current = stepId;
    // Fotografa o que já existe: o registro que sobrar depois é o lançamento
    // real que o usuário acabou de criar no tutorial.
    markTutorialKnownItemIds(TUTORIAL_SCOPE, (allExpenses || []).map((expense) => expense.id));
    setIsCreateDialogOpen(true);
    setFormData({
      // Nome vazio de propósito: o usuário escolhe, na listagem existente,
      // uma despesa verdadeira dele.
      name: '',
      target_amount: String(FIRST_ACCESS_TUTORIAL_DEMO.futureExpense.targetAmount),
      target_date: tutorialTargetDate,
      present_value: String(FIRST_ACCESS_TUTORIAL_DEMO.futureExpense.presentValue),
      monthly_payment: '',
      monthly_rate: systemMonthlyRatePercent.toFixed(2),
      category: tutorialCategory,
      beneficiary_agent_id: defaultExpenseAgentId,
      notes: '',
    });
  }, [
    activeTutorialStep,
    canPlanInPeriod,
    defaultExpenseAgentId,
    systemMonthlyRatePercent,
    tutorialCategory,
    tutorialTargetDate,
    allExpenses,
  ]);

  useEffect(() => {
    const stepId = FIRST_ACCESS_TUTORIAL_STEPS.futureConfirm;

    if (activeTutorialStep !== stepId) {
      if (appliedTutorialStepRef.current === stepId) {
        appliedTutorialStepRef.current = null;
      }
      return;
    }

    if (!tutorialFutureExpense || appliedTutorialStepRef.current === stepId) return;

    appliedTutorialStepRef.current = stepId;
    setSelectedExpense(tutorialFutureExpense);
    setRealizeFormData({
      amount:
        tutorialFutureExpense.planned_contribution ||
        tutorialFutureExpense.monthly_payment ||
        Number(FIRST_ACCESS_TUTORIAL_DEMO.futureExpense.targetAmount),
      realization_date: tutorialContributionDate,
      investment_location: FIRST_ACCESS_TUTORIAL_DEMO.futureExpense.investmentLocation,
      payment_method: '',
      notes: '',
      agent_id: tutorialFutureExpense.beneficiary_agent_id || defaultPayerAgentId || '',
    });
    setIsRealizeDialogOpen(true);
  }, [
    activeTutorialStep,
    defaultPayerAgentId,
    tutorialContributionDate,
    tutorialFutureExpense,
  ]);

  // Defaults when opening the Edit dialog (agente padrão, caso vazio)
  useEffect(() => {
    if (!isEditDialogOpen) return;
    const defaultAgent = expenseAgents.find((agent) => agent.id === defaultExpenseAgentId);
    if (!defaultAgent?.id) return;

    setEditFormData((prev) => {
      if (prev.beneficiary_agent_id) return prev;
      return { ...prev, beneficiary_agent_id: defaultAgent.id };
    });
  }, [defaultExpenseAgentId, expenseAgents, isEditDialogOpen]);

  const monthlyPercentToAnnualPercent = (monthlyPercentRaw: string) => {
    const monthlyPercent = Number(monthlyPercentRaw);
    const safeMonthlyPercent = Number.isFinite(monthlyPercent) ? monthlyPercent : systemMonthlyRatePercent;
    const monthlyDecimal = safeMonthlyPercent / 100;
    const annualPercent = (Math.pow(1 + monthlyDecimal, 12) - 1) * 100;
    return Number.isFinite(annualPercent) ? annualPercent : 0;
  };

  // Preview calculation (create)
  const draftCreateCalcParams = useMemo(() => {
    const annualRate = monthlyPercentToAnnualPercent(formData.monthly_rate);

    const safeNumber = (raw: string) => {
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    };

    const params: Record<string, unknown> = { annual_rate: annualRate };

    // Present value (Tenho)
    if (createCalculatedField !== 'present_value') {
      const pv = safeNumber(formData.present_value || '0') ?? 0;
      params.present_value = pv;
    }

    // Target amount (Quero)
    if (createCalculatedField !== 'target_amount') {
      const fv = safeNumber(formData.target_amount);
      if (!fv || fv <= 0) return null;
      params.target_amount = fv;
    }

    // Target date (Para Quando)
    if (createCalculatedField !== 'target_date') {
      if (!formData.target_date) return null;
      params.target_date = formData.target_date;
    }

    // Monthly payment (Valor a Investir)
    if (createCalculatedField !== 'monthly_payment') {
      const pmt = safeNumber(formData.monthly_payment);
      if (pmt === null || pmt < 0) return null;
      params.monthly_payment = pmt;
    }

    return params as unknown as FutureExpenseCalculationRequest;
  }, [
    createCalculatedField,
    formData.monthly_rate,
    formData.present_value,
    formData.target_amount,
    formData.target_date,
    formData.monthly_payment,
    systemMonthlyRatePercent,
  ]);

  const debouncedDraftCreateCalcParams = useDebounce(draftCreateCalcParams, 250);
  const { data: draftCreateCalc, isLoading: isDraftCreateCalcLoading } =
    useFutureExpenseDraftCalculation(debouncedDraftCreateCalcParams, currentPeriod?.id);

  /** Formulário completo: habilita o envio e recebe o realce verde. */
  const isCreateFormComplete =
    !isDraftCreateCalcLoading &&
    Boolean(draftCreateCalc) &&
    draftCreateCalc?.is_savable !== false &&
    Boolean(formData.name) &&
    Boolean(formData.category) &&
    Boolean(formData.beneficiary_agent_id);

  // Preview calculation (edit)
  const draftEditCalcParams = useMemo(() => {
    const annualRate = monthlyPercentToAnnualPercent(editFormData.monthly_rate);

    const safeNumber = (raw: string) => {
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    };

    const params: Record<string, unknown> = { annual_rate: annualRate };

    if (editCalculatedField !== 'present_value') {
      const pv = safeNumber(editFormData.present_value || '0') ?? 0;
      params.present_value = pv;
    }

    if (editCalculatedField !== 'target_amount') {
      const fv = safeNumber(editFormData.target_amount);
      if (!fv || fv <= 0) return null;
      params.target_amount = fv;
    }

    if (editCalculatedField !== 'target_date') {
      if (!editFormData.target_date) return null;
      params.target_date = editFormData.target_date;
    }

    if (editCalculatedField !== 'monthly_payment') {
      const pmt = safeNumber(editFormData.monthly_payment);
      if (pmt === null || pmt < 0) return null;
      params.monthly_payment = pmt;
    }

    return params as unknown as FutureExpenseCalculationRequest;
  }, [
    editCalculatedField,
    editFormData.monthly_rate,
    editFormData.present_value,
    editFormData.target_amount,
    editFormData.target_date,
    editFormData.monthly_payment,
    systemMonthlyRatePercent,
  ]);

  const debouncedDraftEditCalcParams = useDebounce(draftEditCalcParams, 250);
  const { data: draftEditCalc } =
    useFutureExpenseDraftCalculation(
      debouncedDraftEditCalcParams,
      selectedExpense?.budget_period_id || currentPeriod?.id
    );

  useEffect(() => {
    if (!isEditDialogOpen) return;
    if (editCalculatedField !== 'monthly_payment') return;
    if (draftEditCalc?.monthly_payment === undefined || draftEditCalc?.monthly_payment === null) return;

    setEditFormData((prev) => {
      const nextMonthlyPayment = String(draftEditCalc.monthly_payment);
      if (prev.monthly_payment === nextMonthlyPayment) return prev;
      return { ...prev, monthly_payment: nextMonthlyPayment };
    });
  }, [draftEditCalc?.monthly_payment, editCalculatedField, isEditDialogOpen]);

  // Handlers
  const handleCreateExpense = () => {
    const effectiveTargetAmount =
      createCalculatedField === 'target_amount'
        ? draftCreateCalc?.target_amount
        : Number(formData.target_amount);
    const effectiveTargetDate =
      createCalculatedField === 'target_date'
        ? draftCreateCalc?.target_date
        : formData.target_date;
    const effectivePresentValue =
      createCalculatedField === 'present_value'
        ? draftCreateCalc?.present_value
        : Number(formData.present_value || 0);

    if (
      !formData.name ||
      !formData.category ||
      !formData.beneficiary_agent_id ||
      !draftCreateCalc ||
      draftCreateCalc.is_savable === false ||
      !Number.isFinite(Number(effectiveTargetAmount)) ||
      Number(effectiveTargetAmount) <= 0 ||
      !effectiveTargetDate
    )
      return;

    const annualRate = monthlyPercentToAnnualPercent(formData.monthly_rate);
    
    createExpense({
      data: {
        name: formData.name,
        target_amount: Number(effectiveTargetAmount),
        target_date: String(effectiveTargetDate),
        present_value: Number.isFinite(effectivePresentValue) ? effectivePresentValue : undefined,
        annual_rate: annualRate,
        category: formData.category || undefined,
        beneficiary_agent_id: formData.beneficiary_agent_id || undefined,
        notes: formData.notes || undefined,
      },
      periodId: currentPeriod?.id,
    }, {
      onSuccess: () => {
        requestTutorialAdvance(FIRST_ACCESS_TUTORIAL_STEPS.futureSave);
        // Replanejamento: a despesa de origem só é removida agora que a nova meta
        // foi efetivamente criada. Cancelar antes disso preserva o lançamento.
        if (replanSourceExpenseId) {
          deleteExpense(replanSourceExpenseId);
          setReplanSourceExpenseId(null);
        }
        setIsCreateDialogOpen(false);
      }
    });
  };

  const handleOpenRealizeDialog = (expense: FutureExpense) => {
    setSelectedExpense(expense);
    const preferredPayerAgentId =
      expense.beneficiary_agent_id && payerAgents.some((agent) => agent.id === expense.beneficiary_agent_id)
        ? expense.beneficiary_agent_id
        : '';
    setRealizeFormData({
      amount: expense.planned_contribution || expense.monthly_payment || 0,
      realization_date: format(new Date(), 'yyyy-MM-dd'),
      investment_location: '',
      payment_method: '',
      notes: '',
      agent_id: preferredPayerAgentId || defaultPayerAgentId || '',
    });
    setIsRealizeDialogOpen(true);
  };

  const handleRealizeExpense = () => {
    if (!selectedExpense) return;
    if (!realizeFormData.amount) return;
    if (!realizeFormData.realization_date) return;
    if (!realizeFormData.investment_location?.trim()) return;
    if (!realizeFormData.agent_id) return;

    if (
      !isDateWithinPeriod(
        realizeFormData.realization_date,
        currentPeriod?.start_date,
        currentPeriod?.end_date
      )
    ) {
      toast({
        title: 'Data fora do período orçamentário',
        description: `A data do investimento deve estar dentro do período do lançamento (${formatDatePtBR(
          currentPeriod?.start_date
        )} a ${formatDatePtBR(currentPeriod?.end_date)}).`,
        variant: 'destructive',
      });
      return;
    }

    realizeExpense(
      {
        id: selectedExpense.id,
        data: {
          ...realizeFormData,
        },
      },
      {
        onSuccess: () => {
          requestTutorialAdvance(FIRST_ACCESS_TUTORIAL_STEPS.futureConfirm);
          setIsRealizeDialogOpen(false);
          setSelectedExpense(null);
        },
      }
    );
  };

  const handleOpenReplanDialog = (expense: FutureExpense) => {
    setSelectedExpense(expense);
    setReplanFormData({
      new_target_amount: expense.target_amount.toString(),
      new_target_date: expense.target_date,
      reason: '',
    });
    setIsReplanDialogOpen(true);
  };

  const getSurplusAmount = (expense: FutureExpense) => {
    const targetAmount = Number(expense.target_amount) || 0;
    const accumulatedAmount = Number(expense.accumulated_amount) || 0;
    return Math.max(0, accumulatedAmount - targetAmount);
  };

  const getRevenueAmountForExpense = (expense: FutureExpense) => {
    const accumulatedAmount = Number(expense.accumulated_amount) || 0;
    return accumulatedAmount;
  };

  const formatTodayLabel = () => format(new Date(), 'dd/MM/yyyy');

  const handleReplanFromSurplus = (expense: FutureExpense) => {
    if (!canPlanInPeriod) {
      toast({
        title: 'Ação não permitida',
        description: 'Não é possível excluir/replanejar despesas futuras em períodos orçamentários passados.',
        variant: 'destructive',
      });
      return;
    }

    const surplus = getSurplusAmount(expense);
    // Não excluímos aqui: a despesa de origem é preservada até o usuário CONFIRMAR
    // a nova meta (handleCreateExpense.onSuccess). Cancelar mantém o lançamento.
    setReplanSourceExpenseId(expense.id);
    setFormData({
      name: expense.name,
      target_amount: '',
      target_date: '',
      present_value: surplus ? String(surplus) : '',
      monthly_payment: '',
      monthly_rate: systemMonthlyRatePercent.toFixed(2),
      category: expense.category || '',
      beneficiary_agent_id: expense.beneficiary_agent_id || defaultExpenseAgentId || '',
      notes: `Despesa "${expense.name}" replanejada em ${formatTodayLabel()}`,
    });
    setIsCreateDialogOpen(true);
  };

  const handleAddAsRevenue = (expense: FutureExpense) => {
    if (!canPlanInPeriod) {
      toast({
        title: 'Ação não permitida',
        description: 'Não é possível excluir despesas futuras em períodos orçamentários passados.',
        variant: 'destructive',
      });
      return;
    }

    const amount = getRevenueAmountForExpense(expense);
    // A exclusão da despesa de origem ocorre apenas após a receita ser efetivamente
    // criada na tela de Receitas (via sourceFutureExpenseId). Cancelar por lá
    // preserva a despesa futura com suas Ações.
    navigate('/financial/revenues', {
      state: {
        revenuePrefill: {
          name: expense.name,
          revenue_type: 'single',
          planned_amount: amount,
          receipt_method: 'Sistema',
          expected_date: format(new Date(), 'yyyy-MM-dd'),
          notes: `Receita "${expense.name}" criada a partir de um replanejamento em ${formatTodayLabel()}`,
        },
        sourceFutureExpenseId: expense.id,
      },
    });
  };

  const handleReplanExpense = () => {
    if (!selectedExpense) return;

    replanExpense(
      {
        id: selectedExpense.id,
        data: {
          new_target_date: replanFormData.new_target_date,
          new_target_amount: replanFormData.new_target_amount 
            ? Number.parseFloat(replanFormData.new_target_amount) 
            : undefined,
        },
      },
      {
        onSuccess: () => {
          setIsReplanDialogOpen(false);
          setSelectedExpense(null);
        },
      }
    );
  };

  const handleOpenExecuteDialog = (expense: FutureExpense) => {
    setSelectedExpense(expense);
    setExecuteFormData({
      execution_date: format(new Date(), 'yyyy-MM-dd'),
      actual_amount_spent: Number(expense.target_amount) || 0,
      payment_method: '',
      notes: '',
    });
    setIsExecuteDialogOpen(true);
  };

  const handleExecuteExpense = () => {
    if (!selectedExpense) return;

    executeExpense(
      {
        id: selectedExpense.id,
        data: {
          execution_date: executeFormData.execution_date,
          actual_amount_spent: executeFormData.actual_amount_spent,
          payment_method: executeFormData.payment_method || undefined,
          // Regra: ao transformar, entra como despesa mensal ÚNICA no PO de troca
          create_monthly_expense: true,
          notes: executeFormData.notes || undefined,
        },
      },
      {
        onSuccess: () => {
          setIsExecuteDialogOpen(false);
          setSelectedExpense(null);
        },
      }
    );
  };

  const handleDeleteExpense = (expense: FutureExpense, skipConfirm = false) => {
    if (!canPlanInPeriod) {
      toast({
        title: 'Ação não permitida',
        description: 'Não é possível excluir despesas futuras em períodos orçamentários passados.',
        variant: 'destructive',
      });
      return;
    }

    if (skipConfirm) {
      deleteExpense(expense.id);
      return;
    }

    setExpenseToDelete(expense);
  };

  const handleConfirmDeleteExpense = () => {
    if (!expenseToDelete) return;
    deleteExpense(expenseToDelete.id);
    setExpenseToDelete(null);
  };

  const handleToggleExpenseSelection = (expenseId: string, checked: boolean) => {
    setSelectedExpenseIds((prev) => {
      if (checked) {
        return prev.includes(expenseId) ? prev : [...prev, expenseId];
      }
      return prev.filter((id) => id !== expenseId);
    });
  };

  const handleToggleAllExpenses = (checked: boolean) => {
    if (checked) {
      setSelectedExpenseIds(visibleExpenseIds);
      return;
    }
    setSelectedExpenseIds([]);
  };

  const handleBulkDeleteExpenses = async () => {
    if (selectedVisibleExpenseIds.length === 0) return;

    const idsToDelete = [...selectedVisibleExpenseIds];
    setIsBulkDeleting(true);

    const results = await Promise.allSettled(idsToDelete.map((id) => deleteExpenseAsync(id)));
    const failedIds = idsToDelete.filter((_, index) => results[index]?.status === 'rejected');

    setIsBulkDeleting(false);

    if (failedIds.length === 0) {
      setSelectedExpenseIds([]);
      setIsBulkDeleteDialogOpen(false);
      return;
    }

    setSelectedExpenseIds(failedIds);
  };

  const handleOpenEditDialog = (expense: FutureExpense) => {
    setSelectedExpense(expense);
    setEditCalculatedField('monthly_payment');
    // Converter taxa anual (decimal, ex: 0.12) para mensal (%) com taxa efetiva:
    // monthly% = ((1 + annual_decimal)^(1/12) - 1) * 100
    const annualDecimal = expense.annual_rate ? Number.parseFloat(String(expense.annual_rate)) : 0;
    const monthlyDecimal = annualDecimal > 0 ? Math.pow(1 + annualDecimal, 1 / 12) - 1 : 0;
    const monthlyRate = (monthlyDecimal * 100).toFixed(2);
    setEditFormData({
      name: expense.name,
      target_amount: String(expense.target_amount),
      target_date: expense.target_date,
      present_value: expense.present_value ? String(expense.present_value) : '',
      monthly_payment: expense.monthly_payment ? String(expense.monthly_payment) : '',
      monthly_rate: monthlyRate,
      category: expense.category || '',
      beneficiary_agent_id: expense.beneficiary_agent_id || defaultExpenseAgentId || '',
      notes: expense.notes || '',
    });
    setIsEditDialogOpen(true);
  };

  const handleOpenCalcDialog = (expense: FutureExpense) => {
    setCalcExpenseId(expense.id);
    setIsCalcDialogOpen(true);
  };

  const handleOpenEditRealizedDialog = (expense: FutureExpense) => {
    setSelectedExpense(expense);
    setSelectedRealization(null);
    setIsEditRealizedDialogOpen(true);
  };

  const handleUpdateRealization = () => {
    if (!selectedExpense || !selectedRealization) return;
    
    updateRealization({
      expenseId: selectedExpense.id,
      realizationId: selectedRealization.id,
      data: {
        amount: selectedRealization.amount,
        realization_date: selectedRealization.realization_date || undefined,
        investment_location: selectedRealization.investment_location || undefined,
        agent_id: selectedRealization.agent_id || undefined,
        notes: selectedRealization.notes || undefined,
      }
    }, {
      onSuccess: () => {
        setSelectedRealization(null);
        setSelectedExpense(null);
        setIsEditRealizedDialogOpen(false);
      }
    });
  };

  const handleDeleteRealization = (realization: FutureExpenseRealization) => {
    if (!selectedExpense) return;
    if (!confirm('Tem certeza que deseja excluir esta realização?')) return;
    
    deleteRealization({
      expenseId: selectedExpense.id,
      realizationId: realization.id
    });
  };

  const handleUpdateExpense = () => {
    if (
      !selectedExpense ||
      !editFormData.name ||
      !editFormData.target_amount ||
      !editFormData.target_date ||
      !editFormData.category ||
      !editFormData.beneficiary_agent_id
    )
      return;

    const annualRate = monthlyPercentToAnnualPercent(editFormData.monthly_rate);
    
    updateExpense({
      id: selectedExpense.id,
      data: {
        name: editFormData.name,
        target_amount: Number.parseFloat(editFormData.target_amount),
        target_date: editFormData.target_date,
        present_value: editFormData.present_value ? Number.parseFloat(editFormData.present_value) : undefined,
        annual_rate: annualRate,
        category: editFormData.category || undefined,
        beneficiary_agent_id: editFormData.beneficiary_agent_id || undefined,
        notes: editFormData.notes || undefined,
      },
    }, {
      onSuccess: () => {
        setIsEditDialogOpen(false);
        setSelectedExpense(null);
      },
    });
  };

  // Get displayed expenses based on tab
  const getDisplayedExpenses = () => allExpenses || [];

  // Filter expenses
  const filteredExpenses = getDisplayedExpenses().filter((expense) => {
    const matchesSearch = normalizeSearchText(expense.name).includes(normalizeSearchText(searchTerm));
    const matchesStatus = statusFilter === 'all' || expense.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Sort expenses
  const sortedExpenses = [...filteredExpenses].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' });
        break;
      case 'date':
        comparison = (a.target_date || '') > (b.target_date || '') ? 1 : -1;
        break;
      case 'value':
        comparison = Number(a.target_amount) - Number(b.target_amount);
        break;
      case 'status':
        comparison = (a.status || '').localeCompare(b.status || '');
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  const visibleExpenseIds = sortedExpenses.map((expense) => expense.id);
  const selectedVisibleExpenseIds = selectedExpenseIds.filter((id) => visibleExpenseIds.includes(id));
  const selectedExpenseCount = selectedVisibleExpenseIds.length;
  const allExpensesSelected =
    visibleExpenseIds.length > 0 && selectedExpenseCount === visibleExpenseIds.length;
  const someExpensesSelected = selectedExpenseCount > 0 && !allExpensesSelected;
  const selectedExpenses = sortedExpenses.filter((expense) =>
    selectedVisibleExpenseIds.includes(expense.id)
  );
  const selectedTargetTotal = selectedExpenses.reduce(
    (sum, expense) => sum + Number(expense.target_amount || 0),
    0
  );
  const selectedAccumulatedTotal = selectedExpenses.reduce(
    (sum, expense) => sum + Number(expense.accumulated_amount || 0),
    0
  );
  const isBulkSelection = allExpensesSelected || selectedExpenseCount > 1;
  const bulkDeleteDescription = isBulkSelection
    ? 'Esta ação excluirá todas as despesas futuras cadastradas. Deseja confirmar a exclusão?'
    : 'Esta ação excluirá a despesa futura selecionada. Deseja confirmar a exclusão?';

  useEffect(() => {
    setSelectedExpenseIds((prev) => {
      const next = prev.filter((id) => visibleExpenseIds.includes(id));
      return next.length === prev.length ? prev : next;
    });
  }, [visibleExpenseIds]);

  const actionsRequiredExpenses = (allExpenses || []).filter((expense) => {
    const targetAmount = Number(expense.target_amount) || 0;
    const accumulated = Number(expense.accumulated_amount) || 0;
    const hasReachedValue = targetAmount > 0 && accumulated >= targetAmount;
    const targetDate = parseISO(expense.target_date);
    const isDeadlineReached = Number.isNaN(targetDate.getTime())
      ? false
      : !isBefore(new Date(), targetDate);
    return hasReachedValue || isDeadlineReached || expense.status === 'value_reached' || expense.status === 'expired';
  });

  const isPageLoading = isLoading || isPeriodLoading;

  if (isPageLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando metas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6 max-w-full">
      {/* Header */}
      <div className="flex flex-col">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Despesas Futuras</h1>
          <p className="text-muted-foreground">
            Planeje e acompanhe seu <span className="font-semibold">enriquecimento</span>, desejos e obrigações futuras
          </p>
        </div>
        <div className="mt-8 flex items-center gap-3">
          <PeriodNavigator
            currentPeriod={currentPeriod}
            periods={navigation?.periods || []}
            isLoading={isNavigating}
            onNavigate={navigatePeriod}
            variant="boxed"
          />
        </div>
      </div>

      {/* Financial Summary Cards - sempre o 1º bloco de KPIs e fixo ao rolar */}
      <FinancialSummaryCards periodId={currentPeriod?.id} />

      {/* KPIs */}
      <div className="py-3">
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
          <Card className="sm:w-[240px]">
            <CardContent className="p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="space-y-1 min-w-0">
                  <p className="text-xs text-muted-foreground truncate">Despesas Futuras Ativas</p>
                  <p className="text-lg font-semibold">{activeExpenses?.length || 0}</p>
                </div>
                <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                  <Calendar className="h-4 w-4 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="sm:w-[240px]">
            <CardContent className="p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="space-y-1 min-w-0">
                  <p className="text-xs text-muted-foreground truncate">Despesas com Ações</p>
                  <p className="text-lg font-semibold">{actionsRequiredExpenses.length}</p>
                </div>
                <div className="h-8 w-8 rounded-md bg-amber-50 flex items-center justify-center shrink-0">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Aviso: metas que aguardam ação */}
      {actionsRequiredExpenses.length > 0 && (
        <Alert className="border-amber-500/40 bg-amber-50/60 dark:bg-amber-950/10">
          <AlertDescription>
            Você tem <span className="font-medium">{actionsRequiredExpenses.length}</span>{' '}
            {actionsRequiredExpenses.length === 1 ? 'meta' : 'metas'} que requerem Ações.
          </AlertDescription>
        </Alert>
      )}

      {/* Search */}
      <Card id="sheet">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
            {/* Primary actions row */}
            <div className="flex gap-2 sm:gap-4">
              <Button
                onClick={() => setIsCreateDialogOpen(true)}
                className="gap-2 flex-1 sm:flex-none"
                disabled={!canPlanInPeriod}
                data-tour-id="create-future-expense"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Nova Despesa Futura</span>
                <span className="sm:hidden">Nova</span>
              </Button>
            </div>
            
            {/* Search */}
            <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar despesas futuras..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchTerm.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setSearchTerm('')}
                  aria-label="Limpar busca de despesas futuras"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            {/* Filters row */}
            <div className="flex flex-wrap gap-2 sm:gap-4">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-[140px]">
                  <SelectValue placeholder="Ordenar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Nome (A-Z)</SelectItem>
                  <SelectItem value="date">Data Limite</SelectItem>
                  <SelectItem value="value">Valor da Meta</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="h-10 w-10 shrink-0"
                title={sortDirection === 'asc' ? 'Ordenação crescente' : 'Ordenação decrescente'}
              >
                <ArrowUpDown className={`h-4 w-4 transition-transform ${sortDirection === 'desc' ? 'rotate-180' : ''}`} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {canPlanInPeriod && sortedExpenses.length > 0 && (
        <Card>
          <CardContent className="px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                id="select-all-future-expenses"
                checked={allExpensesSelected ? true : someExpensesSelected ? 'indeterminate' : false}
                onCheckedChange={(checked) => handleToggleAllExpenses(checked === true)}
                aria-label="Selecionar todas as despesas futuras"
              />
              <Label htmlFor="select-all-future-expenses" className="cursor-pointer">
                Selecionar Todos
              </Label>
            </div>
            {selectedExpenseCount > 0 && (
              <div className="flex flex-col gap-3 w-full sm:w-auto sm:flex-row sm:items-center sm:gap-4">
                <BulkSelectionSummary
                  plannedLabel="Objetivo"
                  plannedTotal={selectedTargetTotal}
                  realizedLabel="Acumulado"
                  realizedTotal={selectedAccumulatedTotal}
                />
                <Button
                  variant="destructive"
                  size="sm"
                  className="gap-2 w-full sm:w-auto"
                  onClick={() => setIsBulkDeleteDialogOpen(true)}
                  disabled={isBulkDeleting || isDeleting}
                >
                  <Trash2 className="h-4 w-4" />
                  Excluir ({selectedExpenseCount})
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Expenses Grid */}
      {sortedExpenses.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Target className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-medium mb-1">Nenhuma despesa futura encontrada</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {searchTerm ? 'Tente ajustar a busca' : 'Crie sua primeira despesa futura'}
            </p>
            <Button
              onClick={() => setIsCreateDialogOpen(true)}
              disabled={!canPlanInPeriod}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Despesa Futura
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3 max-h-[calc(100vh-400px)] overflow-y-auto">
          {sortedExpenses.map((expense) => (
            <div key={expense.id} className="relative">
              {canPlanInPeriod && (
                <Checkbox
                  checked={selectedExpenseIds.includes(expense.id)}
                  onCheckedChange={(checked) => handleToggleExpenseSelection(expense.id, checked === true)}
                  aria-label={`Selecionar ${expense.name}`}
                  className="absolute left-4 top-4 z-10"
                />
              )}
              <FutureExpenseRow
                expense={expense}
                referenceDate={currentPeriod?.start_date}
                onRealize={expense.status === 'active' ? () => handleOpenRealizeDialog(expense) : undefined}
                realizeTourId={
                  tutorialFutureExpense?.id === expense.id
                    ? FIRST_ACCESS_TUTORIAL_STEPS.futureInvest
                    : undefined
                }
                disableRealize={!canRealizeInPeriod}
                onReplan={['active', 'value_reached', 'expired'].includes(expense.status) ? () => handleOpenReplanDialog(expense) : undefined}
                onReplanFromSurplus={() => handleReplanFromSurplus(expense)}
                onAddAsRevenue={() => handleAddAsRevenue(expense)}
                onDeleteFromSurplus={() => handleDeleteExpense(expense, true)}
                onExecute={['value_reached', 'expired'].includes(expense.status) ? () => handleOpenExecuteDialog(expense) : undefined}
                onViewDetails={() => handleOpenCalcDialog(expense)}
                onEdit={() => handleOpenEditDialog(expense)}
                onEditRealized={() => handleOpenEditRealizedDialog(expense)}
                onDelete={() => handleDeleteExpense(expense)}
                className={canPlanInPeriod ? 'pl-10 sm:pl-12' : undefined}
              />
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      {filteredExpenses.length > 0 && (
        <div className="text-sm text-muted-foreground text-center">
          Exibindo {filteredExpenses.length} de {allExpenses?.length || 0} metas
        </div>
      )}

      <Dialog open={isBulkDeleteDialogOpen} onOpenChange={setIsBulkDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Excluir despesas selecionadas</DialogTitle>
            <DialogDescription>
              {bulkDeleteDescription}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkDeleteDialogOpen(false)} disabled={isBulkDeleting}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDeleteExpenses}
              disabled={isBulkDeleting || selectedExpenseCount === 0}
            >
              {isBulkDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                'Excluir'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm single expense deletion */}
      <Dialog
        open={expenseToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setExpenseToDelete(null);
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Tem certeza que deseja excluir a despesa futura?</DialogTitle>
            <DialogDescription>
              A despesa irá sair dos POs futuros, mas continuará nos POs passados.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpenseToDelete(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleConfirmDeleteExpense}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Expense Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent data-tour-id="future-create-dialog" className="w-[95vw] max-w-[750px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova Despesa Futura</DialogTitle>
            <DialogDescription>
              São os sonhos, metas e obrigações futuras. Planeje-os para ganhar juros no
              processo de acumulação e para pagar à vista com descontos - viagens, troca do
              carro, impostos etc. Veja a listagem.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 px-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Nome*</Label>
                <Combobox
                  id="name"
                  mode="input"
                  value={formData.name}
                  onChange={(value) => setFormData({ ...formData, name: value })}
                  portalled={false}
                  options={(expenseNamesList?.items || []).map((item) => ({
                    value: item.name,
                    label: item.name,
                  }))}
                  placeholder="Nome"
                  emptyText="Nenhum nome encontrado"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="present_value" className="flex items-center gap-2">
                  Investimento Inicial
                  {createCalculatedField === 'present_value' && <Badge variant="secondary">Calculado</Badge>}
                  {!canSetInitialInvestment && <Badge variant="outline">Indisponível no plano</Badge>}
                </Label>
                <CurrencyInput
                  id="present_value"
                  value={
                    !canSetInitialInvestment
                      ? 0
                      : createCalculatedField === 'present_value'
                        ? Number(draftCreateCalc?.present_value || 0)
                        : Number.parseFloat(formData.present_value) || 0
                  }
                  onChange={(value) => setFormData({ ...formData, present_value: String(value) })}
                  disabled={!canSetInitialInvestment || createCalculatedField === 'present_value'}
                />
                {!canSetInitialInvestment && (
                  <p className="text-xs text-muted-foreground">
                    Funcionalidade não disponível no seu plano. O valor é fixado em R$ 0,00.
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="target_amount" className="flex items-center gap-2">
                  Quero
                  {createCalculatedField === 'target_amount' && <Badge variant="secondary">Calculado</Badge>}
                </Label>
                <CurrencyInput
                  id="target_amount"
                  value={
                    createCalculatedField === 'target_amount'
                      ? Number(draftCreateCalc?.target_amount || 0)
                      : Number.parseFloat(formData.target_amount) || 0
                  }
                  onChange={(value) => setFormData({ ...formData, target_amount: String(value) })}
                  disabled={createCalculatedField === 'target_amount'}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="target_date" className="flex items-center gap-2">
                  Para Quando
                  {createCalculatedField === 'target_date' && <Badge variant="secondary">Calculado</Badge>}
                </Label>
                <DateInput
                  id="target_date"
                  value={createCalculatedField === 'target_date' ? (draftCreateCalc?.target_date || '') : formData.target_date}
                  onChange={(value) => setFormData({ ...formData, target_date: value })}
                  placeholder="dd/mm/aaaa"
                  mode="future-only"
                  showSingleCalendar
                  disabled={createCalculatedField === 'target_date'}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="monthly_rate" className="flex items-center gap-2">
                  Taxa de Juros (a.m.)
                  {!canSetInterestRate && <Badge variant="outline">Indisponível no plano</Badge>}
                </Label>
                <PercentageInput
                  id="monthly_rate"
                  value={!canSetInterestRate ? systemMonthlyRatePercent : Number.parseFloat(formData.monthly_rate) || 0}
                  onChange={(value) => setFormData({ ...formData, monthly_rate: value.toString() })}
                  disabled={!canSetInterestRate}
                />
                {!canSetInterestRate && (
                  <p className="text-xs text-muted-foreground">
                    Funcionalidade não disponível no seu plano. Usa a taxa padrão do sistema ({systemMonthlyRateLabel}).
                  </p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="monthly_payment" className="flex items-center gap-2">
                  Valor a Investir
                  {createCalculatedField === 'monthly_payment' && <Badge variant="secondary">Calculado</Badge>}
                </Label>
                <CurrencyInput
                  id="monthly_payment"
                  value={Number(draftCreateCalc?.monthly_payment || 0)}
                  onChange={() => undefined}
                  disabled
                />
              </div>
            </div>

            {draftCreateCalc?.warning && (
              <Alert variant="destructive">
                <AlertTitle>Não é possível salvar</AlertTitle>
                <AlertDescription>{draftCreateCalc.warning}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="category">Categoria*</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger id="category">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriesList?.items?.map((item) => (
                      <SelectItem key={item.id} value={item.name}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="beneficiary_agent_id">Agente*</Label>
                <Select
                  value={formData.beneficiary_agent_id}
                  onValueChange={(value) => setFormData({ ...formData, beneficiary_agent_id: value })}
                >
                  <SelectTrigger id="beneficiary_agent_id">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name} {agent.is_default_agent ? '(Padrão)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="notes">Observações</Label>
              <Textarea
                id="notes"
                placeholder="Escreva aqui..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateExpense}
              data-tour-id={FIRST_ACCESS_TUTORIAL_STEPS.futureSave}
              // Formulário completo fica verde: sinaliza que já pode criar.
              className={isCreateFormComplete && !isCreating ? CREATE_READY_BUTTON_CLASS : undefined}
              disabled={isCreating || !isCreateFormComplete}
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Criando...
                </>
              ) : (
                'Criar Meta'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="w-[95vw] max-w-[750px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              Editar Despesa Futura
            </DialogTitle>
            <DialogDescription>
              Atualize os dados da despesa futura "{selectedExpense?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 px-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit_name">Nome*</Label>
                <Combobox
                  id="edit_name"
                  mode="input"
                  options={(expenseNamesList?.items || []).map((item) => ({
                    value: item.name,
                    label: item.name,
                  }))}
                  value={editFormData.name}
                  onChange={(value) => setEditFormData({ ...editFormData, name: value })}
                  portalled={false}
                  placeholder="Nome"
                  emptyText="Nenhum nome encontrado"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit_present_value" className="flex items-center gap-2">
                  Investimento Inicial
                  {!canSetInitialInvestment && <Badge variant="outline">Indisponível no plano</Badge>}
                </Label>
                <CurrencyInput
                  id="edit_present_value"
                  value={!canSetInitialInvestment ? 0 : Number.parseFloat(editFormData.present_value) || 0}
                  onChange={(value) => setEditFormData({ ...editFormData, present_value: String(value) })}
                  disabled={!canSetInitialInvestment}
                />
                {!canSetInitialInvestment && (
                  <p className="text-xs text-muted-foreground">
                    Funcionalidade não disponível no seu plano. O valor é fixado em R$ 0,00.
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit_target_amount">Quero*</Label>
                <CurrencyInput
                  id="edit_target_amount"
                  value={Number.parseFloat(editFormData.target_amount) || 0}
                  onChange={(value) => setEditFormData({ ...editFormData, target_amount: String(value) })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit_target_date">Para Quando*</Label>
                <DateInput
                  id="edit_target_date"
                  value={editFormData.target_date}
                  onChange={(value) => setEditFormData({ ...editFormData, target_date: value })}
                  placeholder="dd/mm/aaaa"
                  mode="future-only"
                  showSingleCalendar
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit_monthly_rate" className="flex items-center gap-2">
                  Taxa de Juros (a.m.)
                  {!canSetInterestRate && <Badge variant="outline">Indisponível no plano</Badge>}
                </Label>
                <PercentageInput
                  id="edit_monthly_rate"
                  value={!canSetInterestRate ? systemMonthlyRatePercent : Number.parseFloat(editFormData.monthly_rate) || 0}
                  onChange={(value) => setEditFormData({ ...editFormData, monthly_rate: value.toString() })}
                  disabled={!canSetInterestRate}
                />
                {!canSetInterestRate && (
                  <p className="text-xs text-muted-foreground">
                    Funcionalidade não disponível no seu plano. Usa a taxa padrão do sistema ({systemMonthlyRateLabel}).
                  </p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="edit_monthly_payment">Valor a Investir*</Label>
                <CurrencyInput
                  id="edit_monthly_payment"
                  value={Number(editFormData.monthly_payment || 0)}
                  onChange={() => {}}
                  disabled
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit_category">Categoria*</Label>
                <Select
                  value={editFormData.category}
                  onValueChange={(value) => setEditFormData({ ...editFormData, category: value })}
                >
                  <SelectTrigger id="edit_category">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriesList?.items?.map((item) => (
                      <SelectItem key={item.id} value={item.name}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit_beneficiary_agent_id">Agente*</Label>
                <Select
                  value={editFormData.beneficiary_agent_id}
                  onValueChange={(value) => setEditFormData({ ...editFormData, beneficiary_agent_id: value })}
                >
                  <SelectTrigger id="edit_beneficiary_agent_id">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name} {agent.is_default_agent ? '(Padrão)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit_notes">Observações</Label>
              <Textarea
                id="edit_notes"
                placeholder="Escreva aqui..."
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpdateExpense}
              disabled={
                isUpdating ||
                !editFormData.name ||
                !editFormData.target_amount ||
                !editFormData.target_date ||
                !editFormData.category ||
                !editFormData.beneficiary_agent_id
              }
            >
              {isUpdating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Realize (Investment) Dialog */}
      <Dialog open={isRealizeDialogOpen} onOpenChange={setIsRealizeDialogOpen}>
        <DialogContent data-tour-id="future-realize-dialog" className="w-[95vw] max-w-[750px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-primary" />
              Registrar Investimento
            </DialogTitle>
            <DialogDescription>
              Registre uma contribuição para "{selectedExpense?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 px-2">
            {/* Summary Card */}
            <Card className="bg-muted/50">
              <CardContent className="py-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Objetivo:</span>
                  <CurrencyDisplay value={selectedExpense?.target_amount || 0} className="font-medium" />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Acumulado:</span>
                  <CurrencyDisplay value={selectedExpense?.accumulated_amount || 0} className="font-medium" />
                </div>
                <Separator />
                <div className="flex justify-between text-sm font-medium">
                  <span>Faltam:</span>
                  <CurrencyDisplay 
                    value={(selectedExpense?.target_amount || 0) - (selectedExpense?.accumulated_amount || 0)} 
                    colorize 
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Valor a Investir:</span>
                  <CurrencyDisplay value={selectedExpense?.planned_contribution || selectedExpense?.monthly_payment || 0} className="text-primary font-medium" />
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="realize_date">Data do Investimento*</Label>
                <DateInput
                  id="realize_date"
                  value={realizeFormData.realization_date}
                  onChange={(value) => setRealizeFormData({ ...realizeFormData, realization_date: value })}
                  placeholder="Selecione a data"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="realize_investment_location">Local do Investimento*</Label>
                <Combobox
                  id="realize_investment_location"
                  mode="input"
                  options={(investmentInstitutions || []).map((i) => ({ value: i.name, label: i.name }))}
                  value={realizeFormData.investment_location || ''}
                  onChange={(value) => setRealizeFormData({ ...realizeFormData, investment_location: value })}
                  onCustomSelect={(typed) => {
                    const trimmed = typed.trim();
                    if (!trimmed) return;
                    const exists = (investmentInstitutions || []).some(
                      (i) => i.name.toLowerCase() === trimmed.toLowerCase()
                    );
                    if (exists) return;

                    createInvestmentInstitution(
                      { name: trimmed },
                      {
                        onSuccess: (created) => {
                          setRealizeFormData((prev) => ({
                            ...prev,
                            investment_location: created.name,
                          }));
                        },
                      }
                    );
                  }}
                  placeholder="Digite ou selecione a instituição"
                  searchPlaceholder="Buscar instituição..."
                  emptyText="Nenhuma instituição encontrada."
                  allowCustom
                  customLabel="Adicionar"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="realize_amount">Valor do Investimento*</Label>
                <CurrencyInput
                  id="realize_amount"
                  value={realizeFormData.amount || 0}
                  onChange={(value) => setRealizeFormData({ ...realizeFormData, amount: value })}
                />
                <p className="text-xs text-muted-foreground">
                  O sistema irá acrescentar os juros proporcionais ao período
                </p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="agent_id">Quem Investiu*</Label>
                <Select
                  value={realizeFormData.agent_id || ''}
                  onValueChange={(value) => setRealizeFormData({ ...realizeFormData, agent_id: value })}
                >
                  <SelectTrigger id="agent_id">
                    <SelectValue placeholder="Selecione o agente" />
                  </SelectTrigger>
                  <SelectContent>
                    {payerAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name} {agent.is_default_agent ? '(Padrão)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="realize_notes">Observações (opcional)</Label>
              <Textarea
                id="realize_notes"
                placeholder="Ex: Aplicação no Tesouro Direto..."
                value={realizeFormData.notes || ''}
                onChange={(e) => setRealizeFormData({ ...realizeFormData, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRealizeDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleRealizeExpense}
              data-tour-id={FIRST_ACCESS_TUTORIAL_STEPS.futureConfirm}
              disabled={
                isRealizing ||
                !realizeFormData.amount ||
                !realizeFormData.realization_date ||
                !realizeFormData.investment_location?.trim() ||
                !realizeFormData.agent_id
              }
            >
              {isRealizing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Investindo...
                </>
              ) : (
                <>
                  <Wallet className="h-4 w-4 mr-2" />
                  Investir
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replan Dialog */}
      <Dialog open={isReplanDialogOpen} onOpenChange={setIsReplanDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-primary" />
              Replanejar Meta
            </DialogTitle>
            <DialogDescription>
              Ajuste o valor ou prazo de "{selectedExpense?.name}". O sistema recalculará a parcela mensal.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Current status */}
            <Card className="bg-muted/50">
              <CardContent className="py-3 space-y-2">
                <h4 className="text-sm font-medium">Status atual:</h4>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Objetivo:</span>
                  <CurrencyDisplay value={selectedExpense?.target_amount || 0} />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Acumulado:</span>
                  <CurrencyDisplay value={selectedExpense?.accumulated_amount || 0} />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Data limite:</span>
                  <span>
                    {selectedExpense?.target_date 
                      ? formatBrasilia(selectedExpense.target_date, 'dd/MM/yyyy')
                      : '-'}
                  </span>
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-2">
              <Label htmlFor="new_target_amount">Novo valor da meta (R$)</Label>
              <CurrencyInput
                id="new_target_amount"
                value={Number.parseFloat(replanFormData.new_target_amount) || 0}
                onChange={(value) => setReplanFormData({ ...replanFormData, new_target_amount: String(value) })}
              />
              <p className="text-xs text-muted-foreground">
                Diminuir o valor reduz a parcela mensal
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new_target_date">Nova data limite</Label>
              <DateInput
                id="new_target_date"
                value={replanFormData.new_target_date}
                onChange={(value) => setReplanFormData({ ...replanFormData, new_target_date: value })}
                placeholder="Selecione a data"
              />
              <p className="text-xs text-muted-foreground">
                Aumentar o prazo reduz a parcela mensal
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="replan_reason">Motivo do replanejamento (opcional)</Label>
              <Input
                id="replan_reason"
                placeholder="Ex: Ajuste de orçamento..."
                value={replanFormData.reason}
                onChange={(e) => setReplanFormData({ ...replanFormData, reason: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReplanDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleReplanExpense} disabled={isReplanning}>
              {isReplanning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Replanejando...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Replanejar Meta
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Execute / Transform Dialog */}
      <Dialog open={isExecuteDialogOpen} onOpenChange={setIsExecuteDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-5 w-5 text-primary" />
              Executar
            </DialogTitle>
            <DialogDescription>
              Ao transformar, o valor acumulado entra como <span className="font-medium">receita</span> e o gasto entra
              automaticamente como <span className="font-medium">despesa mensal única</span> no PO de troca.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Card className="bg-muted/50">
              <CardContent className="py-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Meta:</span>
                  <span className="font-medium">{selectedExpense?.name || '-'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Acumulado:</span>
                  <CurrencyDisplay value={selectedExpense?.accumulated_amount || 0} />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Objetivo:</span>
                  <CurrencyDisplay value={selectedExpense?.target_amount || 0} />
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="execution_date">Data da troca</Label>
                <DateInput
                  id="execution_date"
                  value={executeFormData.execution_date}
                  onChange={(value) => setExecuteFormData({ ...executeFormData, execution_date: value })}
                  placeholder="Selecione a data"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="actual_amount_spent">Valor gasto (R$)</Label>
                <CurrencyInput
                  id="actual_amount_spent"
                  value={executeFormData.actual_amount_spent}
                  onChange={(value) => setExecuteFormData({ ...executeFormData, actual_amount_spent: value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="execution_payment_method">Forma de pagamento (opcional)</Label>
                <Select
                  value={executeFormData.payment_method}
                  onValueChange={(value) => setExecuteFormData({ ...executeFormData, payment_method: value })}
                >
                  <SelectTrigger id="execution_payment_method">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(paymentMethodsList?.items || []).map((item) => (
                      <SelectItem key={item.id} value={item.name}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="execution_notes">Observações (opcional)</Label>
                <Textarea
                  id="execution_notes"
                  placeholder="Ex: Compra realizada..."
                  value={executeFormData.notes}
                  onChange={(e) => setExecuteFormData({ ...executeFormData, notes: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsExecuteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleExecuteExpense} disabled={isExecuting || !selectedExpense}>
              {isExecuting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Transformando...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Transformar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Realized Dialog */}
      <Dialog
        open={isEditRealizedDialogOpen}
        onOpenChange={(open) => {
          setIsEditRealizedDialogOpen(open);
          if (!open) {
            setSelectedExpense(null);
            setSelectedRealization(null);
          }
        }}
      >
        <DialogContent className="w-[95vw] max-w-[650px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-primary" />
              Editar Realizado
            </DialogTitle>
            <DialogDescription>
              Edite os investimentos realizados para "{selectedExpense?.name}"
            </DialogDescription>
          </DialogHeader>

          {isRealizationsLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : expenseRealizations && expenseRealizations.length > 0 ? (
            <div className="space-y-4">
              {/* Summary Card */}
              <Card className="bg-muted/50">
                <CardContent className="py-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Objetivo:</span>
                    <CurrencyDisplay value={selectedExpense?.target_amount || 0} className="font-medium" />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Acumulado:</span>
                    <CurrencyDisplay value={selectedExpense?.accumulated_amount || 0} className="font-medium" colorize />
                  </div>
                </CardContent>
              </Card>

              {/* Realizations List */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Investimentos Realizados</Label>
                {expenseRealizations.map((realization) => (
                  <Card key={realization.id} className="border">
                    <CardContent className="py-3">
                      {selectedRealization?.id === realization.id ? (
                        /* Edit mode */
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="edit_realization_date">Data do Investimento*</Label>
                              <DateInput
                                id="edit_realization_date"
                                value={selectedRealization.realization_date || ''}
                                onChange={(value) => setSelectedRealization({ ...selectedRealization, realization_date: value })}
                                placeholder="dd/mm/aaaa"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="edit_investment_location">Local do Investimento*</Label>
                              <Combobox
                                id="edit_investment_location"
                                mode="input"
                                options={(investmentInstitutions || []).map((i) => ({ value: i.name, label: i.name }))}
                                value={selectedRealization.investment_location || ''}
                                onChange={(value) => setSelectedRealization({ ...selectedRealization, investment_location: value })}
                                onCustomSelect={(typed) => {
                                  const trimmed = typed.trim();
                                  if (!trimmed) return;
                                  const exists = (investmentInstitutions || []).some(
                                    (i) => i.name.toLowerCase() === trimmed.toLowerCase()
                                  );
                                  if (exists) return;
                                  createInvestmentInstitution(
                                    { name: trimmed },
                                    {
                                      onSuccess: (created) => {
                                        setSelectedRealization((prev) => prev ? { ...prev, investment_location: created.name } : null);
                                      },
                                    }
                                  );
                                }}
                                placeholder="Digite ou selecione a instituição"
                                searchPlaceholder="Buscar instituição..."
                                emptyText="Nenhuma instituição encontrada."
                                allowCustom
                                customLabel="Adicionar"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="edit_realization_amount">Valor do Investimento*</Label>
                              <CurrencyInput
                                id="edit_realization_amount"
                                value={selectedRealization.amount || 0}
                                onChange={(value) => setSelectedRealization({ ...selectedRealization, amount: value })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="edit_realization_agent">Quem Investiu*</Label>
                              <Select
                                value={selectedRealization.agent_id || ''}
                                onValueChange={(value) => setSelectedRealization({ ...selectedRealization, agent_id: value })}
                              >
                                <SelectTrigger id="edit_realization_agent">
                                  <SelectValue placeholder="Selecione o agente" />
                                </SelectTrigger>
                                <SelectContent>
                                  {payerAgents.map((agent) => (
                                    <SelectItem key={agent.id} value={agent.id}>
                                      {agent.name} {agent.is_default_agent ? '(Padrão)' : ''}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="edit_realization_notes">Observações (opcional)</Label>
                            <Textarea
                              id="edit_realization_notes"
                              placeholder="Ex: Aplicação no Tesouro Direto..."
                              value={selectedRealization.notes || ''}
                              onChange={(e) => setSelectedRealization({ ...selectedRealization, notes: e.target.value })}
                            />
                          </div>

                          <div className="flex justify-end gap-2 pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedRealization(null)}
                            >
                              Cancelar
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleUpdateRealization}
                              disabled={isUpdatingRealization || !selectedRealization.amount || !selectedRealization.realization_date}
                            >
                              {isUpdatingRealization ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                'Salvar'
                              )}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* View mode */
                        <div className="flex items-center justify-between">
                          <div className="space-y-1.5 flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <CurrencyDisplay value={realization.amount || 0} className="font-medium" />
                              {realization.realization_date && (
                                <span className="text-xs text-muted-foreground">
                                  em {format(parseISO(realization.realization_date), 'dd/MM/yyyy', { locale: ptBR })}
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                              {realization.investment_location && (
                                <span>Local: {realization.investment_location}</span>
                              )}
                              {realization.agent_id && agents && (
                                <span>
                                  Investidor: {agents.find(a => a.id === realization.agent_id)?.name || '-'}
                                </span>
                              )}
                            </div>
                            {realization.notes && (
                              <p className="text-xs text-muted-foreground italic truncate">{realization.notes}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 ml-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setSelectedRealization(realization)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => handleDeleteRealization(realization)}
                              disabled={isDeletingRealization}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Wallet className="h-12 w-12 text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium mb-1">Nenhum investimento realizado</h3>
              <p className="text-sm text-muted-foreground">
                Ainda não há investimentos registrados para esta despesa futura.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditRealizedDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Planilha de Realização (cálculos) */}
      <Dialog
        open={isCalcDialogOpen}
        onOpenChange={(open) => {
          setIsCalcDialogOpen(open);
          if (!open) setCalcExpenseId('');
        }}
      >
        <DialogContent className="w-[95vw] max-w-[720px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Planilha de Realização (Juros)
            </DialogTitle>
            <DialogDescription>
              Projeção do <span className="font-medium">TENHO</span> no início do próximo PO usando{' '}
              <span className="font-medium">FV = PV*(1+i)^n</span>.
            </DialogDescription>
          </DialogHeader>

          {(() => {
            if (isRealizationSheetLoading) return (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
            );
            if (realizationSheet) return (
                <div className="space-y-4">
                  <Card className="bg-muted/50">
                    <CardContent className="py-3 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">PO atual:</span>
                        <span className="font-medium">
                          {formatBrasilia(realizationSheet.period_start, 'dd/MM/yyyy')} -{' '}
                          {formatBrasilia(realizationSheet.period_end, 'dd/MM/yyyy')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Início do próximo PO:</span>
                        <span className="font-medium">
                          {formatBrasilia(realizationSheet.next_period_start, 'dd/MM/yyyy')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Taxa mensal (decimal):</span>
                        <span className="font-medium">{Number(realizationSheet.monthly_rate).toFixed(6)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Taxa diária (decimal):</span>
                        <span className="font-medium">{Number(realizationSheet.daily_rate).toFixed(12)}</span>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="border rounded-lg overflow-hidden">
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted text-xs font-medium">
                      <div className="col-span-4">Item</div>
                      <div className="col-span-2">Data</div>
                      <div className="col-span-2 text-right">PV</div>
                      <div className="col-span-2 text-right">n (dias)</div>
                      <div className="col-span-2 text-right">FV</div>
                    </div>
                    <div className="divide-y">
                      {realizationSheet.items.map((item, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 text-sm">
                          <div className="col-span-4">{item.label}</div>
                          <div className="col-span-2 text-muted-foreground">
                            {formatBrasilia(item.realization_date, 'dd/MM')}
                          </div>
                          <div className="col-span-2 text-right">
                            <CurrencyDisplay value={Number(item.present_value)} size="xs" />
                          </div>
                          <div className="col-span-2 text-right">{item.days}</div>
                          <div className="col-span-2 text-right">
                            <CurrencyDisplay value={Number(item.future_value)} size="xs" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">TENHO no próximo PO</span>
                    <CurrencyDisplay value={Number(realizationSheet.total_next_period_amount)} />
                  </div>
                </div>
            );
            return (
                <p className="text-sm text-muted-foreground">Nenhum cálculo disponível.</p>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default FutureExpensesPage;
